import { getBackendApiUrl } from './apiConfig';

// G2 Travel API Service — Supabase Edge Functions backend proxy (avoid CORS)

const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Helper function to get headers with Supabase auth if needed
const getHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
  };

  // Add Supabase auth header if using Supabase Edge Functions
  // Supabase Edge Functions accept Authorization Bearer token
  if (getBackendApiUrl().includes('supabase.co') && SUPABASE_ANON_KEY) {
    headers['Authorization'] = `Bearer ${SUPABASE_ANON_KEY}`;
  }

  return headers;
};

// Log the API URL being used (only in development)
if (process.env.NODE_ENV === 'development') {
  const base = getBackendApiUrl();
  console.log('🔗 Backend API URL:', base);
  console.log('📡 Using Supabase Edge Functions:', base.includes('supabase.co'));
  console.log('🔑 Supabase Auth:', SUPABASE_ANON_KEY ? 'Configured' : 'Missing');
}

/**
 * G2 Travel API Configuration
 */
export const G2_CONFIG = {
  baseUrl: 'https://staging.g2-travel.com:8443/api/json',
  credentials: {
    organisation: 'TOURASCOTEST',
    username: 'apiuser',
    password: 'QEpxNVu1nA',
  },
  distributionId: 'PL1662', // API requires max 6 characters
};

// Simple in-memory caches to avoid hammering the backend/G2 API
let hotelsCache = null;
let hotelsCacheTimestamp = 0;
let propertiesCache = null;
let propertiesCacheTimestamp = 0;
let citiesCache = null;
let citiesCacheTimestamp = 0;
let hotelsPromise = null;
let propertiesPromise = null;
let citiesPromise = null;
const STATIC_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/** Short-lived cache for GET /api/g2/hotels/:id — speeds repeat visits and back/forward. */
const hotelDetailsCache = new Map(); // hotelId -> { ts, data }
const HOTEL_DETAILS_CACHE_TTL_MS = 5 * 60 * 1000;

/** Dedupe parallel hero-search catalog loads (server caches the heavy G2 payloads for all users). */
let searchCatalogFetchPromise = null;

/** Browser copy: instant search suggestions on repeat visits (same Chrome profile). Server cache still speeds the refresh request. */
const SEARCH_CATALOG_LS_KEY = 'rezholidays_search_catalog_v4';
const SEARCH_CATALOG_LS_VERSION = 4;
const SEARCH_CATALOG_CLIENT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
/** ~35k compact hotels + cities can exceed 4.5MB; align hero search with full /api/search-catalog snapshot. */
const SEARCH_CATALOG_LS_MAX_BYTES = 14_000_000;

const compactCityForSearch = (city) => ({
  id: city?.id ?? city?.cityId ?? city?.code ?? '',
  cityId: city?.cityId ?? city?.id ?? city?.code ?? '',
  name: (city?.name || city?.cityName || city?.city || '').toString().trim(),
  cityName: (city?.cityName || city?.name || city?.city || '').toString().trim(),
  code: (city?.code || city?.cityId || city?.id || '').toString(),
  countryName: (city?.countryName || '').toString(),
  countryCode: (city?.countryCode || '').toString(),
});

const compactHotelForSearch = (hotel) => ({
  id: (hotel?.id ?? hotel?.propertyId ?? hotel?.code ?? '').toString(),
  propertyId: (hotel?.propertyId ?? hotel?.id ?? hotel?.code ?? '').toString(),
  code: (hotel?.code || '').toString(),
  name: (hotel?.name || hotel?.propertyName || hotel?.hotelName || '').toString().trim(),
  city: (hotel?.city || hotel?.cityName || '').toString(),
  cityName: (hotel?.cityName || hotel?.city || '').toString(),
  countryName: (hotel?.countryName || '').toString(),
  countryCode: (hotel?.countryCode || '').toString(),
  cityId: (hotel?.cityId || '').toString(),
});

const persistSearchCatalogToLocalStorage = (cities, hotelsShaped) => {
  if (typeof localStorage === 'undefined') return;
  try {
    const citiesCompact = (Array.isArray(cities) ? cities : []).map(compactCityForSearch);
    const hotelsCompactFull = (Array.isArray(hotelsShaped) ? hotelsShaped : []).map(compactHotelForSearch);
    let hotelsCompact = hotelsCompactFull;

    const buildPayload = (hotelsSlice, extra = {}) => ({
      v: SEARCH_CATALOG_LS_VERSION,
      ts: Date.now(),
      cities: citiesCompact,
      hotels: hotelsSlice,
      ...extra,
    });

    let payload = buildPayload(hotelsCompact);
    let s = JSON.stringify(payload);

    // If too large, progressively trim hotels (keep as many as fit — same catalog as /resultssss when possible).
    if (s.length > SEARCH_CATALOG_LS_MAX_BYTES) {
      const targetSizes = [
        60000, 50000, 45000, 40000, 35000, 32000, 28000, 25000, 20000, 16000, 12000, 8000, 5000, 3000,
      ];
      for (const size of targetSizes) {
        hotelsCompact = hotelsCompactFull.slice(0, size);
        payload = buildPayload(hotelsCompact);
        s = JSON.stringify(payload);
        if (s.length <= SEARCH_CATALOG_LS_MAX_BYTES) break;
      }
    }

    if (s.length > SEARCH_CATALOG_LS_MAX_BYTES) {
      // Cannot fit even a trimmed list: store cities only + flag so hero refetches full hotels from the API.
      const stub = buildPayload([], { hotels_deferred: true });
      try {
        localStorage.setItem(SEARCH_CATALOG_LS_KEY, JSON.stringify(stub));
      } catch (e2) {
        console.warn('Search catalog stub persist failed:', e2);
      }
      console.warn(
        'Search catalog too large for localStorage; saved cities only. Full hotel list loads from /api/search-catalog on open.'
      );
      return;
    }

    localStorage.setItem(SEARCH_CATALOG_LS_KEY, s);
  } catch (e) {
    console.warn('Could not persist search catalog to localStorage:', e);
  }
};

/**
 * Synchronous read for first paint — use in useState(() => …) so the bar has data before network.
 */
export const getSearchCatalogSync = () => {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(SEARCH_CATALOG_LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.v !== SEARCH_CATALOG_LS_VERSION || !parsed.ts) return null;
    if (Date.now() - parsed.ts > SEARCH_CATALOG_CLIENT_MAX_AGE_MS) return null;
    // Incomplete cache: must fetch full hotels from network (matches /resultssss page).
    if (parsed.hotels_deferred) return null;
    const cities = Array.isArray(parsed.cities) ? parsed.cities : [];
    const hotels = Array.isArray(parsed.hotels) ? parsed.hotels : [];
    return { cities, hotels, savedAt: parsed.ts };
  } catch {
    return null;
  }
};

export const clearSearchCatalogLocalStorage = () => {
  try {
    localStorage.removeItem(SEARCH_CATALOG_LS_KEY);
  } catch (e) {
    /* ignore */
  }
};

const isCacheValid = (timestamp) => {
  if (!timestamp) return false;
  return Date.now() - timestamp < STATIC_CACHE_DURATION;
};

const isTransientTransportError = (message) => {
  const m = (message || '').toLowerCase();
  return (
    m.includes('connection reset by peer') ||
    m.includes('tls handshake eof') ||
    m.includes('connection refused') ||
    m.includes('networkerror') ||
    m.includes('failed to fetch') ||
    m.includes('error sending request')
  );
};

const toHotelSearchShape = (raw) => {
  const id = raw.id ?? raw.code ?? raw.propertyId;
  if (id == null || String(id).trim() === '') return null;
  return {
    id: String(id),
    propertyId: raw.propertyId != null ? String(raw.propertyId) : String(id),
    code: raw.code,
    name: (raw.name || raw.propertyName || raw.hotelName || '').toString().trim(),
    city: raw.city,
    cityName: raw.cityName,
    countryName: raw.countryName,
    countryCode: raw.countryCode,
    cityId: raw.cityId,
  };
};

const fetchSearchCatalogFromSupabaseSnapshot = async () => {
  const response = await fetch(`${getBackendApiUrl()}/api/search-catalog`, {
    method: 'GET',
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch search catalog snapshot: HTTP ${response.status}`);
  }
  const result = await response.json();
  const cities = Array.isArray(result?.cities) ? result.cities : [];
  const hotels = Array.isArray(result?.hotels) ? result.hotels : [];
  const fetchedAt = result?.fetchedAt ? Date.parse(result.fetchedAt) : Date.now();
  return { cities, hotels, fetchedAt: Number.isFinite(fetchedAt) ? fetchedAt : Date.now() };
};

/**
 * Cities + slim hotel list for autocomplete. Backend (Express or Edge) serves cached G2 data to every user.
 */
export const fetchSearchCatalogFromNetwork = async () => {
  if (searchCatalogFetchPromise) {
    return searchCatalogFetchPromise;
  }

  searchCatalogFetchPromise = (async () => {
    try {
      // Primary source: Supabase snapshot table (pre-fetched every 24h by refresh job).
      try {
        const snap = await fetchSearchCatalogFromSupabaseSnapshot();
        if (snap.cities.length > 0 || snap.hotels.length > 0) {
          persistSearchCatalogToLocalStorage(snap.cities, snap.hotels);
          return { cities: snap.cities, hotels: snap.hotels };
        }
      } catch (snapErr) {
        console.warn('search-catalog snapshot unavailable, using G2 fallback:', snapErr);
      }

      // Fallback: live endpoints (kept for resiliency).
      const [citiesData, hotelsResponse] = await Promise.all([
        getCities().catch(() => []),
        getAllHotels({}).catch(() => ({ success: false, hotels: [] })),
      ]);

      const citiesList = Array.isArray(citiesData) ? citiesData : [];
      let hotelsList =
        hotelsResponse?.success && Array.isArray(hotelsResponse.hotels)
          ? hotelsResponse.hotels
          : [];
      if (hotelsList.length === 0) {
        const fb = await getProperties().catch(() => []);
        hotelsList = Array.isArray(fb) ? fb : [];
      }

      const hotelsShaped = hotelsList.map(toHotelSearchShape).filter(Boolean);

      persistSearchCatalogToLocalStorage(citiesList, hotelsShaped);

      return { cities: citiesList, hotels: hotelsShaped };
    } catch (err) {
      console.error('fetchSearchCatalogFromNetwork failed:', err);
      return { cities: [], hotels: [] };
    } finally {
      searchCatalogFetchPromise = null;
    }
  })();

  return searchCatalogFetchPromise;
};

/**
 * GET /api/search-catalog only — raw Supabase snapshot (no live G2 fallback).
 * Same payload shape the hero search reads first.
 */
export const fetchSearchCatalogSnapshotRaw = async () => {
  const response = await fetch(`${getBackendApiUrl()}/api/search-catalog`, {
    method: 'GET',
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch search catalog snapshot: HTTP ${response.status}`);
  }
  return response.json();
};

/**
 * Search hotel prices (Step 1)
 */
export const searchPrices = async (searchParams) => {
  try {
    const response = await fetch(`${getBackendApiUrl()}/api/g2/price-search`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(searchParams),
    });

    const result = await response.json();
    
    if (!response.ok) {
      // Handle error response
      const errorMsg = result.error?.description || result.error?.code || result.error || 'Failed to search prices';
      throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
    
    // Check if result has an error field (some APIs return 200 with error object)
    if (result.error) {
      const errorMsg = result.error.description || result.error.code || 'Search failed';
      throw new Error(errorMsg);
    }

    return result;
  } catch (error) {
    console.error('Error searching prices:', error);
    // If error is already a string, use it; otherwise convert to string
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(typeof error === 'string' ? error : JSON.stringify(error));
  }
};

/**
 * Check availability (Step 2)
 */
export const checkAvailability = async (availabilityParams) => {
  try {
    const response = await fetch(`${getBackendApiUrl()}/api/g2/availability`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(availabilityParams),
    });

    const result = await response.json();
    
    if (!response.ok) {
      const errorMsg = result.error?.description || result.error?.code || result.error || 'Failed to check availability';
      throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
    
    if (result.error) {
      const errorMsg = result.error.description || result.error.code || 'Availability check failed';
      throw new Error(errorMsg);
    }

    return result;
  } catch (error) {
    console.error('Error checking availability:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(typeof error === 'string' ? error : JSON.stringify(error));
  }
};

/**
 * Create booking (Step 3)
 */
export const createBooking = async (params) => {
  try {
    console.log('📤 g2TravelApi - Received params:', params);
    console.log('📤 g2TravelApi - params.passenger:', params.passenger);
    console.log('📤 g2TravelApi - params.passenger[0]:', params.passenger?.[0]);
    console.log('📤 g2TravelApi - params.passenger[0].lead:', params.passenger?.[0]?.lead);
    
    // Log each passenger's lead field
    if (params.passenger) {
      params.passenger.forEach((p, i) => {
        console.log(`📤 g2TravelApi - Passenger ${i + 1}:`, p);
        console.log(`📤 g2TravelApi - Passenger ${i + 1} lead:`, p.lead, 'Type:', typeof p.lead);
        console.log(`📤 g2TravelApi - Passenger ${i + 1} has lead?`, 'lead' in p);
      });
    }

    const bodyString = JSON.stringify(params);
    console.log('📤 g2TravelApi - Request body string:', bodyString);

    // Check if 'lead' is in the string
    if (!bodyString.includes('"lead"')) {
      console.error('🔴 CRITICAL: lead field is MISSING from JSON string!');
    } else {
      console.log('✅ lead field IS present in JSON string');
    }

    // ✅ Send params AS-IS, don't modify
    const response = await fetch(`${getBackendApiUrl()}/api/g2/booking`, {
      method: 'POST',
      headers: getHeaders(),
      body: bodyString
    });

    const data = await response.json();
    
    console.log('📥 g2TravelApi - Response status:', response.status);
    console.log('📥 g2TravelApi - Response:', data);

    // ✅ Check if response contains error (even if HTTP was 200)
    if (data.error) {
      console.error('❌ API error in result:', data.error);
      const errorMsg = data.error.description || data.error.code || 'Booking failed';
      throw new Error(errorMsg);
    }

    // ✅ Also check if response is NOT ok
    if (!response.ok) {
      console.error('❌ API error response (HTTP not ok):', data);
      const errorMsg = data.error?.description || data.error?.code || data.error || `HTTP ${response.status}`;
      throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }

    console.log('✅ g2TravelApi - Booking successful:', data);
    
    return data;
  } catch (error) {
    console.error('❌ g2TravelApi error:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(typeof error === 'string' ? error : JSON.stringify(error));
  }
};

/**
 * Get static data - cities
 */
export const getCities = async () => {
  try {
    // Cache to avoid repeated calls across components.
    if (citiesCache && isCacheValid(citiesCacheTimestamp)) {
      return citiesCache;
    }

    // Deduplicate concurrent requests.
    if (citiesPromise) {
      return await citiesPromise;
    }

    citiesPromise = (async () => {
      const response = await fetch(`${getBackendApiUrl()}/api/g2/cities`, {
        method: 'GET',
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch cities');
      }

      const result = await response.json();
      const data = result.data || [];
      citiesCache = data;
      citiesCacheTimestamp = Date.now();
      return data;
    })();

    const data = await citiesPromise;
    return data;
  } catch (error) {
    console.error('Error fetching cities:', error);
    return [];
  } finally {
    citiesPromise = null;
  }
};

/**
 * Get static data - countries
 */
export const getCountries = async () => {
  try {
    const response = await fetch(`${getBackendApiUrl()}/api/g2/countries`, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch countries');
    }

    const result = await response.json();
    return result.data || [];
  } catch (error) {
    console.error('Error fetching countries:', error);
    return [];
  }
};

/**
 * Get static data - properties
 */
export const getProperties = async () => {
  try {
    // Use cache to avoid repeated calls across components
    if (propertiesCache && isCacheValid(propertiesCacheTimestamp)) {
      return propertiesCache;
    }

    // Deduplicate concurrent requests.
    if (propertiesPromise) {
      return await propertiesPromise;
    }

    propertiesPromise = (async () => {
      const response = await fetch(`${getBackendApiUrl()}/api/g2/properties`, {
        method: 'GET',
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch properties');
      }

      const result = await response.json();
      const data = result.data || [];
      propertiesCache = data;
      propertiesCacheTimestamp = Date.now();
      return data;
    })();

    return await propertiesPromise;
  } catch (error) {
    console.error('Error fetching properties:', error);
    return [];
  } finally {
    propertiesPromise = null;
  }
};

/**
 * Get all hotels from G2 Travel API
 */
export const getAllHotels = async (filters = {}) => {
  const hasFilters = !!(filters.countryCode || filters.cityId);
  const isUnfiltered = !hasFilters;

  // Deduplicate concurrent unfiltered requests (most sections call getAllHotels({})).
  if (isUnfiltered) {
    // If no filters, try to serve from cache first
    if (hotelsCache && isCacheValid(hotelsCacheTimestamp)) {
      return hotelsCache;
    }

    if (hotelsPromise) {
      return await hotelsPromise;
    }

    hotelsPromise = (async () => {
      const queryParams = new URLSearchParams();
      // (filters are empty for this path, but keep logic for safety)
      if (filters.countryCode) queryParams.append('countryCode', filters.countryCode);
      if (filters.cityId) queryParams.append('cityId', filters.cityId);

      const url = `${getBackendApiUrl()}/api/g2/hotels${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

      console.log('📤 Fetching hotels from:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders()
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle rate limiting gracefully
        if (response.status === 429) {
          const errorMsg = data.error?.description || data.error?.code || data.error || 'Rate limit exceeded. Please try again in a few moments.';
          console.warn('⚠️ G2 hotels rate limit:', errorMsg);

          const fallback = await getProperties();
          if (fallback && fallback.length > 0) {
            const result = {
              success: true,
              hotels: fallback,
              count: fallback.length,
              fromFallback: true,
            };
            // Cache fallback so we don't immediately hit 429 again.
            hotelsCache = result;
            hotelsCacheTimestamp = Date.now();
            return result;
          }

          return { success: false, hotels: [], count: 0, rateLimited: true };
        }

        const errorMsg = data.error?.description || data.error?.code || data.error || `HTTP error! status: ${response.status}`;
        throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
      }

      // If backend served fallback data (snapshot/empty), keep UI functional.
      if (data?.fallback) {
        console.warn('⚠️ getAllHotels fallback used:', data.fallback, data.error || '');
      } else if (data.error) {
        const errorMsg = data.error.description || data.error.code || 'Failed to fetch hotels';
        throw new Error(errorMsg);
      }

      console.log('📥 Received hotels:', data.count);

      hotelsCache = data;
      hotelsCacheTimestamp = Date.now();
      return data;
    })();

    try {
      return await hotelsPromise;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (message.includes('Maximum request rate exceeded') || message.includes('Rate limit')) {
        console.warn('⚠️ Swallowing G2 hotels rate-limit error:', message);
        const fallback = await getProperties();
        if (fallback && fallback.length > 0) {
          const result = {
            success: true,
            hotels: fallback,
            count: fallback.length,
            fromFallback: true,
          };
          hotelsCache = result;
          hotelsCacheTimestamp = Date.now();
          return result;
        }
        return { success: false, hotels: [], count: 0, rateLimited: true };
      }
      if (isTransientTransportError(message)) {
        console.warn('⚠️ Swallowing transient transport error for hotels:', message);
        const fallback = await getProperties();
        if (fallback && fallback.length > 0) {
          const result = {
            success: true,
            hotels: fallback,
            count: fallback.length,
            fromFallback: true,
            transientNetworkError: true,
          };
          hotelsCache = result;
          hotelsCacheTimestamp = Date.now();
          return result;
        }
        return { success: false, hotels: [], count: 0, transientNetworkError: true };
      }

      console.error('❌ Error fetching hotels:', error);
      throw error;
    } finally {
      hotelsPromise = null;
    }
  }

  // Filtered path: execute directly (no global dedupe cache for filtered requests).
  try {
    const queryParams = new URLSearchParams();
    if (filters.countryCode) queryParams.append('countryCode', filters.countryCode);
    if (filters.cityId) queryParams.append('cityId', filters.cityId);
    if (filters.location) queryParams.append('location', filters.location);

    const url = `${getBackendApiUrl()}/api/g2/hotels${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

    console.log('📤 Fetching hotels from:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders()
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 429) {
        const errorMsg = data.error?.description || data.error?.code || data.error || 'Rate limit exceeded. Please try again in a few moments.';
        console.warn('⚠️ G2 hotels rate limit:', errorMsg);

        const fallback = await getProperties();
        if (fallback && fallback.length > 0) {
          return {
            success: true,
            hotels: fallback,
            count: fallback.length,
            fromFallback: true,
          };
        }
        return { success: false, hotels: [], count: 0, rateLimited: true };
      }

      const errorMsg = data.error?.description || data.error?.code || data.error || `HTTP error! status: ${response.status}`;
      throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }

    if (data?.fallback) {
      console.warn('⚠️ getAllHotels fallback used:', data.fallback, data.error || '');
    } else if (data.error) {
      const errorMsg = data.error.description || data.error.code || 'Failed to fetch hotels';
      throw new Error(errorMsg);
    }

    console.log('📥 Received hotels:', data.count);
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (message.includes('Maximum request rate exceeded') || message.includes('Rate limit')) {
      console.warn('⚠️ Swallowing G2 hotels rate-limit error:', message);
      const fallback = await getProperties();
      if (fallback && fallback.length > 0) {
        return {
          success: true,
          hotels: fallback,
          count: fallback.length,
          fromFallback: true,
        };
      }
      return { success: false, hotels: [], count: 0, rateLimited: true };
    }
    if (isTransientTransportError(message)) {
      console.warn('⚠️ Swallowing transient transport error for filtered hotels:', message);
      const fallback = await getProperties();
      if (fallback && fallback.length > 0) {
        return {
          success: true,
          hotels: fallback,
          count: fallback.length,
          fromFallback: true,
          transientNetworkError: true,
        };
      }
      return { success: false, hotels: [], count: 0, transientNetworkError: true };
    }
    console.error('❌ Error fetching hotels:', error);
    throw error;
  }
};

/**
 * Combined hotels from G2 + Duffel (Duffel requires coordinates + dates).
 * This is additive and does not replace existing G2-only flows.
 */
export const getCombinedHotels = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    const accepted = [
      'countryCode',
      'cityId',
      'distributionId',
      'location',
      'includeDuffel',
      'checkInDate',
      'checkOutDate',
      'adults',
      'children',
      'rooms',
      'latitude',
      'longitude',
      'radiusKm',
    ];
    accepted.forEach((key) => {
      if (params[key] !== undefined && params[key] !== null && String(params[key]).trim() !== '') {
        queryParams.append(key, String(params[key]));
      }
    });
    const url = `${getBackendApiUrl()}/api/hotels${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(),
    });
    const data = await response.json();
    if (!response.ok) {
      const errorMsg = data?.error || `HTTP ${response.status}`;
      throw new Error(errorMsg);
    }
    return data;
  } catch (error) {
    console.error('❌ Error fetching combined hotels:', error);
    throw error;
  }
};

const parseResponsePayload = async (response) => {
  const raw = await response.text();
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return { error: raw };
  }
};

/**
 * Duffel-only search wrapper (via backend/edge proxy).
 */
export const duffelSearch = async (params = {}) => {
  try {
    const response = await fetch(`${getBackendApiUrl()}/api/duffel/search`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(params),
    });
    const data = await parseResponsePayload(response);
    if (!response.ok) {
      const errorMsg = data?.error || `HTTP ${response.status}`;
      throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
    return data;
  } catch (error) {
    console.error('❌ Duffel search failed:', error);
    throw error;
  }
};

export const duffelFetchRates = async (searchResultId, payload = {}) => {
  try {
    if (!searchResultId) throw new Error('searchResultId is required');
    const response = await fetch(`${getBackendApiUrl()}/api/duffel/search-results/${searchResultId}/rates`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
    const data = await parseResponsePayload(response);
    if (!response.ok) {
      const errorMsg = data?.error || `HTTP ${response.status}`;
      throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
    return data;
  } catch (error) {
    console.error('❌ Duffel rates fetch failed:', error);
    throw error;
  }
};

export const duffelCreateQuote = async (rateId) => {
  try {
    const response = await fetch(`${getBackendApiUrl()}/api/duffel/quotes`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ rate_id: rateId }),
    });
    const data = await parseResponsePayload(response);
    if (!response.ok) {
      const errorMsg = data?.error || `HTTP ${response.status}`;
      throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
    return data;
  } catch (error) {
    console.error('❌ Duffel quote failed:', error);
    throw error;
  }
};

export const duffelCreateBooking = async (payload = {}) => {
  try {
    const response = await fetch(`${getBackendApiUrl()}/api/duffel/bookings`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
    const data = await parseResponsePayload(response);
    if (!response.ok) {
      const errorMsg = data?.error || `HTTP ${response.status}`;
      throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
    return data;
  } catch (error) {
    console.error('❌ Duffel booking failed:', error);
    throw error;
  }
};

/**
 * Get single hotel details by ID
 */
export const getHotelDetails = async (hotelId) => {
  try {
    const key = String(hotelId || '').trim();
    if (!key) {
      throw new Error('Hotel ID is required');
    }

    const cached = hotelDetailsCache.get(key);
    const now = Date.now();
    if (cached && now - cached.ts < HOTEL_DETAILS_CACHE_TTL_MS) {
      return cached.data;
    }

    console.log('📤 Fetching hotel details for:', hotelId);

    const response = await fetch(`${getBackendApiUrl()}/api/g2/hotels/${hotelId}`, {
      method: 'GET',
      headers: getHeaders()
    });

    const data = await response.json();

    if (!response.ok) {
      // Handle rate limiting gracefully
      if (response.status === 429) {
        const errorMsg = data.error?.description || data.error?.code || data.error || 'Rate limit exceeded. Please try again in a few moments.';
        throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
      }
      
      // Handle other errors - extract error message from error object
      const errorMsg = data.error?.description || data.error?.code || data.error || `HTTP error! status: ${response.status}`;
      throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
    
    // Check if result has an error field (some APIs return 200 with error object)
    if (data.error) {
      const errorMsg = data.error.description || data.error.code || 'Failed to fetch hotel details';
      throw new Error(errorMsg);
    }
    
    console.log('📥 Hotel details received:', data.hotel?.name);
    console.log('📥 Rooms received:', data.rooms?.length);

    if (data?.success && data.hotel) {
      hotelDetailsCache.set(key, { ts: Date.now(), data });
    }

    return data;

  } catch (error) {
    console.error('❌ Error fetching hotel details:', error);
    throw error;
  }
};

/**
 * Get minimum price for a hotel
 */
export const getMinimumPrice = async (
  hotelId,
  checkInDate = null,
  nights = 1,
  adults = 1,
  children = 0,
  nationality = 'GB',
  currency = 'GBP'
) => {
  try {
    // Use 7 days from now as default check-in date if not provided
    if (!checkInDate) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 7); // 7 days from now
      checkInDate = tomorrow.toISOString().split('T')[0];
    }

    const searchParams = {
      distributionId: "PL1662",
      nationality,
      checkInDate: checkInDate,
      nights: nights,
      currency,
      propertyId: Array.isArray(hotelId) ? hotelId : [hotelId], // API expects array
      roomPax: [{
        id: 1,
        adults: Number(adults) || 1,
        children: Number(children) || 0
      }],
      includeRoomNames: false,
      includeRoomCapacity: false,
      includeResultsHavingNoCancelPolicy: true,
      includeMSP: true,
      includeSupplements: false,
      timeoutMillis: 30000
    };

    const response = await fetch(`${getBackendApiUrl()}/api/g2/price-search`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(searchParams)
    });

    if (!response.ok) {
      throw new Error('Price fetch failed');
    }

    const data = await response.json();

    if (data.error) {
      console.warn('Price search returned error for hotel', hotelId, ':', data.error);
      return null;
    }

    if (data.propertyOption && data.propertyOption.length > 0) {
      const property = data.propertyOption[0];
      
      if (property.option && property.option.length > 0) {
        // Get minimum price from all options
        const prices = property.option
          .map(opt => opt.totalPrice)
          .filter(price => price != null && price > 0);
        
        if (prices.length === 0) {
          return null;
        }
        
        const minPrice = Math.min(...prices);
        const minOption = property.option.find(opt => opt.totalPrice === minPrice);
        const minSellingPrice = minOption?.minSellingPrice || minPrice;
        
        return {
          minPrice: minPrice,
          minSellingPrice: minSellingPrice,
          currency: currency
        };
      }
    }

    return null;

  } catch (error) {
    console.error('Error fetching minimum price for hotel', hotelId, ':', error);
    return null;
  }
};

export default {
  searchPrices,
  checkAvailability,
  createBooking,
  getCities,
  getCountries,
  getProperties,
  getAllHotels,
  getCombinedHotels,
  duffelSearch,
  duffelFetchRates,
  duffelCreateQuote,
  duffelCreateBooking,
  getHotelDetails,
  getMinimumPrice,
  fetchSearchCatalogFromNetwork,
  fetchSearchCatalogSnapshotRaw,
  getSearchCatalogSync,
  clearSearchCatalogLocalStorage,
  G2_CONFIG,
};
